import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { LineWatch } from './LineWatch';
import { getPusher, pusher as pusherSingleton } from '~/services/websocket';
import { useHasFooter } from '~/contexts/useHasFooter';
import { LineDataProps } from '~/services/queries/line/useLineData';
import { add, format } from 'date-fns';

import { useLineWatchController } from '~/hooks/useLineWatchController';
import NotFound from '~/components/404';
import { Loading } from '~/components/Loading';

const Controller = () => {
  const {
    loading,
    error,
    lineData,
    unitData,
    banners,
    prediction,
    hasFooter,
    shared,
    tolerance,
  } = useLineWatchController();
  const [lineUpdated, setLineUpdated] = useState<LineDataProps | null>(null);
  const [predictionTime, setPredictionTime] = useState({
    minTime: '',
    maxTime: '',
  });
  const { setHasFooter } = useHasFooter();
  const router = useRouter();

  useEffect(() => {
    setHasFooter(hasFooter);
  }, [hasFooter]);

  useEffect(() => {
    if (
      lineData?.status.includes('canceled') ||
      lineData?.status.includes('seated') ||
      lineUpdated?.status.includes('canceled') ||
      lineUpdated?.status.includes('seated')
    ) {
      router.push('/disabled-line');
    }
  }, [lineData, lineUpdated]);

  useEffect(() => {
    if (!lineData?.id) return;

    const client = getPusher() ?? pusherSingleton;
    if (!client) return;

    const channelName = `user.line.${lineData.id}`;
    const channel = client.subscribe(channelName);

    const handler = (data: { line: LineDataProps }) => {
      setLineUpdated(data.line);
    };

    channel.bind('line.sync', handler);

    return () => {
      try {
        channel.unbind('line.sync', handler);
        client.unsubscribe(channelName);
      } catch {}
    };
  }, [lineData?.id]);

  const handleEditTime = (time: number) =>
    format(add(new Date(), { minutes: time }), 'HH:mm');

  useEffect(() => {
    if (prediction) {
      const dateInLocal = localStorage.getItem(
        `range-waiting-time-${lineData}`
      );

      if (dateInLocal) {
        const dateInLocalValues = JSON.parse(dateInLocal);

        if (
          dateInLocalValues.timeInMinutes[0] === prediction[0] &&
          dateInLocalValues.timeInMinutes[1] === prediction[1]
        ) {
          setPredictionTime({
            minTime: dateInLocalValues.minTime,
            maxTime: dateInLocalValues.maxTime,
          });
          return;
        } else {
          const minTime = handleEditTime(prediction[0]);
          const maxTime = handleEditTime(prediction[1]);

          setPredictionTime({ minTime, maxTime });
          localStorage.setItem(
            `range-waiting-time-${lineData?.id}`,
            JSON.stringify({
              minTime: minTime,
              maxTime: maxTime,
              timeInMinutes: prediction,
            })
          );
        }
      } else {
        const minTime = handleEditTime(prediction[0]);
        const maxTime = handleEditTime(prediction[1]);

        setPredictionTime({ minTime, maxTime });
        localStorage.setItem(
          `range-waiting-time-${lineData?.id}`,
          JSON.stringify({
            minTime: minTime,
            maxTime: maxTime,
            timeInMinutes: prediction,
          })
        );
      }
    }
  }, [prediction]);

  if (!router.isReady || loading) {
    return <Loading />;
  }

  if (error) {
    return <NotFound />;
  }

  if (!lineData || !unitData) {
    return <Loading />;
  }

  return (
    <LineWatch
      lineData={lineUpdated || lineData}
      unitData={unitData}
      shared={shared}
      banners={banners ?? []}
      prediction={predictionTime}
      hasFooter={hasFooter}
      tolerance={tolerance}
    />
  );
};

export default Controller;
